import React, {useContext, useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Grid,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";

import {
  findPartnersByEmail,
  requestAccess,
  authenticateAdminUser,
} from "../../services/PartnerSelectionService";
import { getCurrentSession } from "../../helpers/common-apis";
import { useDebounce } from "../../hooks/UseDebounce";
import {
  KEY_AD_USER,
  KEY_HAS_ONGOING_ADMIN_LOGIN,
  KEY_PARTNER_SELECTION
} from "../../helpers/Constants";
import {saveToStorage} from "../../services/LocalStorage";
import {AppContext} from "../../contexts/AppContext";
import {getJWTPayload} from "../../helpers/Util";

const PartnerSelectionPage = () => {
  const { setAuth } = useContext(AppContext);
  const history = useHistory();
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [aDUser, setADUser] = useState(null);
  const [requestingPartner, setRequestingPartner] = useState(null);
  const [requestedPartners, setRequestedPartners] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  useDebounce(
      () => {
        if (sessionData && hasInitialized) {
          searchPartners(sessionData, searchQuery || "");
        }
      },
      500,
      [searchQuery, sessionData, hasInitialized]
  );

  const isPartnerSelectable = (partner) =>
      partner && partner.accessRequestStatus === "APPROVED";

  const isPartnerRequested = (partner) =>
      (partner && partner.accessRequestStatus === "REQUESTED") ||
      requestedPartners.has(partner.id);

  useEffect(() => {
    setIsLoading(true);

    const checkSession = async () => {
      saveToStorage(KEY_HAS_ONGOING_ADMIN_LOGIN, false);
      try{
        const session = await getCurrentSession();
        if (session?.idToken?.payload) {
          setADUser({
            name: (session.idToken.payload.name) || "",
            email: (session.idToken.payload.email) || "",
          });
          setSessionData(session);
          setHasInitialized(true);
          await searchPartners(session, "");
        }
      } catch (e){
        console.error('error checking session', e);
        history.replace("/login");
      } finally {
        setIsLoading(false);
      }
    };

    setTimeout(() => checkSession(), 1000);
  }, []);

  const searchPartners = async (session, searchTerm) => {
    const email = session?.idToken?.payload?.email;

    const hasAtSymbol = (searchTerm || "").includes("@");
    const hasDot = (searchTerm || "").includes(".");
    const isEmail = hasAtSymbol && hasDot;

    // Validate email only when user typed "@"
    if (hasAtSymbol) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(searchTerm)) return;
    }

    const partnerList = await findPartnersByEmail(
        email,
        isEmail ? searchTerm : "",
        isEmail ? "" : searchTerm
    );
    setPartners(partnerList || []);
  };

  const handleInputChange = (_event, value, reason) => {
    if (reason === "input") {
      setSearchQuery(value);
      if (
          selectedPartner &&
          value !== `${selectedPartner.name} (${selectedPartner.id})`
      ) {
        setSelectedPartner(null);
      }
    } else if (reason === "clear") {
      setSearchQuery("");
      setSelectedPartner(null);
      if (sessionData && hasInitialized) {
        searchPartners(sessionData, "");
      }
    }
  };

  const handleRequestAccess = async (partner) => {
    try {
      const session = await getCurrentSession();
      const email = session?.idToken?.payload?.email;

      setRequestingPartner(partner.id);
      setError(null);
      setSuccessMessage(null);

      try {
        await requestAccess({
          adUserEmail: email,
          partnerId: partner.id,
          domainName: "Grubtech",
        });
        setRequestedPartners((prev) => new Set([...(prev || []), partner.id]));
        setSuccessMessage(
            `Partner access request sent for ${partner.name}`
        );
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (e) {
        setError(`Failed to request access for ${partner.name}. Please try again.`
        );
      } finally {
        setRequestingPartner(null);
      }
    } catch (e) {
      setError("Failed to get authentication information");
    }
  };

  const handleContinue = async () => {
    if (!selectedPartner) return;

    if (!isPartnerSelectable(selectedPartner)) {
      setError("Selected partner is not approved for access. Please select an approved partner.");
      return;
    }

    setIsAuthenticating(true);
    setError(null);

    try {
       const adminUserData = await authenticateAdminUser({
        adfsUserEmail: aDUser ? aDUser.email : "",
        partnerId: selectedPartner.id,
        name: aDUser ? aDUser.name : "",
      });
       if(!adminUserData?.idToken){
          setError("Failed to authenticate with selected partner. Please try again.");
         return;
       }
      saveToStorage(KEY_PARTNER_SELECTION, selectedPartner);
      saveToStorage(KEY_AD_USER, aDUser);
      const payload = getJWTPayload(adminUserData.idToken);
      const authUserData = {
        username: payload?.name ?? "",
        userDataKey: payload?.userDataKey ?? "",
        idTokenPayload: payload
      }
      localStorage.setItem('dashboard_user', JSON.stringify({...authUserData}));
      setAuth({...authUserData});
      history.replace("/kitchenselect");
    } catch (e) {
      setError("Failed to authenticate with selected partner. Please try again.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
      <Grid container component="main" style={{ height: "100vh", width: "100vw" }}>
        <Grid
            item
            xs={12}
            component={Paper}
            elevation={0}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#f5f5f5",
            }}
        >
          <Box
              style={{
                width: "100%",
                maxWidth: "600px",
                marginLeft: 32,
                marginRight: 32,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                backgroundColor: "white",
                padding: 32,
                borderRadius: 8,
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
          >
            <Typography variant="h6" component="h1" gutterBottom align="center">
              Choose a Partner Account
            </Typography>

            {error && (
                <Alert severity="error" style={{ width: "100%", marginBottom: 16 }}>
                  {error}
                </Alert>
            )}

            {successMessage && (
                <Alert
                    severity="success"
                    style={{ width: "100%", marginBottom: 16 }}
                >
                  {successMessage}
                </Alert>
            )}

            <Box style={{ width: "100%", marginBottom: 24 }}>
              <Autocomplete
                  options={partners}
                  getOptionLabel={(option) => `${option.name} (${option.id})`}
                  value={selectedPartner}
                  inputValue={searchQuery}
                  onInputChange={(event, value, reason) => {
                    if (!selectedPartner || reason === "clear") {
                      handleInputChange(event, value, reason);
                    }
                  }}
                  disableClearable={false}
                  onChange={(_, newValue) => {
                    if (newValue && isPartnerSelectable(newValue)) {
                      setSelectedPartner(newValue);
                      setSearchQuery(`${newValue.name}`);
                      setError(null);
                    } else if (newValue && !isPartnerSelectable(newValue)) {
                      setSelectedPartner(null);
                      setSearchQuery("");
                    } else {
                      setSelectedPartner(null);
                      setSearchQuery("");
                      setError(null);
                    }
                  }}
                  disabled={isLoading || isAuthenticating}
                  loading={isLoading}
                  filterOptions={(options) => options}
                  renderInput={(params) => (
                      <TextField
                          {...params}
                          label={isLoading? "Loading...":"Search for Partner Account"}
                          variant="outlined"
                          placeholder={
                            hasInitialized
                                ? "Search by name, email or ID"
                                : "Loading..."
                          }
                          InputProps={{
                            ...params.InputProps,
                            readOnly: !!selectedPartner,
                            endAdornment: params.InputProps.endAdornment,
                          }}
                      />
                  )}
                  renderOption={(option) => {
                    const isSelectable = isPartnerSelectable(option);
                    const isRequesting = requestingPartner === option.id;
                    const isRequested = isPartnerRequested(option);

                    return (
                        <Box
                            style={{
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              opacity: isSelectable ? 1 : 0.7,
                              cursor: isSelectable ? "pointer" : "default",
                              padding: "8px 12px",
                            }}
                        >
                          <Box style={{ flex: 1 }}>
                            <Typography variant="body1" style={{ fontWeight: 700 }}>
                              {option.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {option.id}
                            </Typography>
                          </Box>

                          {!isSelectable && (
                              <Box style={{ display: "flex", alignItems: "center" }}>
                                {isRequested ? (
                                    <Typography variant="body2" style={{ fontSize: 12, fontWeight: 500 }}>
                                      Request Sent
                                    </Typography>
                                ) : (
                                    <Button
                                        variant="text"
                                        size="small"
                                        onMouseDown={(e) => e.preventDefault()} // ✅ keeps dropdown open / prevents selection weirdness
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRequestAccess(option);
                                        }}
                                        disabled={isRequesting}
                                        style={{
                                          color: "#0350f7",
                                          backgroundColor: "#9bb8f7",
                                          fontWeight: 500,
                                          minWidth: "auto",
                                          padding: "4px 8px",
                                          fontSize: 12,
                                        }}
                                    >
                                      {isRequesting ? (
                                          <>
                                            <CircularProgress size={12} style={{ marginRight: 4 }} />
                                            Requesting...
                                          </>
                                      ) : (
                                          "Request"
                                      )}
                                    </Button>
                                )}
                              </Box>
                          )}
                        </Box>
                    );
                  }}
                  noOptionsText={
                    isLoading
                        ? "Loading partners..."
                        : searchQuery
                            ? `No partners found matching \"${searchQuery}\"`
                            : partners.length === 0
                                ? "No partners available"
                                : "Type to search partners..."
                  }
              />
            </Box>

            <Box style={{ display: "flex", gap: 16, width: "100%" }}>
              <Button
                  variant="contained"
                  onClick={handleContinue}
                  disabled={
                      !selectedPartner ||
                      !isPartnerSelectable(selectedPartner || {}) ||
                      isAuthenticating
                  }
                  style={{ flex: 2 }}
              >
                {isAuthenticating ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      Authenticating...
                    </>
                ) : (<>Continue</>)}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
  );
};

export default PartnerSelectionPage;
